import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid } from '@mui/material';

import {
    useChangeable,
    FullForm,
    TextField,
    AutocompleteField,
    BoolField
} from '../CommonComponents/Form';
import { Popup, SectionDivider } from '../CommonComponents';

import {
    createGroup,
    deleteGroup,
    getGroup,
    updateGroup
} from '../../api-admin/groups';

import toast from '../../utils/toast';
import { getTenant, searchTenants } from '../../api-admin/tenants';

const defaultGroup = {
    ownerTenantId: null,
    name: '',
    description: '',
    isPublic: false,
    canPublishDefault: false
};

export default function GroupForm() {
    const { id, tenantId } = useParams();
    const navigate = useNavigate();

    const [entity, onChange] = useChangeable(defaultGroup);
    const [showWatchlistDeletionInfo, setShowWatchlistDeletionInfo] = React.useState(false);

    React.useEffect(() => {
        (async () => {
            if (id) {
                const g = await getGroup(id);
                onChange(g);
            } else if (tenantId) {
                const tenant = await getTenant(tenantId);
                onChange({
                    ownerTenantId: tenant.id,
                    ownerTenantName: tenant.name,
                    ownerTenantOrgNumber: tenant.orgNumber
                });
            }
        })();
    }, [id, onChange, tenantId]);

    const onSubmit = React.useCallback(async () => {
        try {
            if (id) {
                await updateGroup(id, entity);
                toast('Gruppen uppdaterad');
                navigate(-1);
            } else {
                const createdGroup = await createGroup(entity);
                toast('Gruppen skapad');
                navigate(`/groups/${createdGroup.id}`, { replace: true });
            }
        } catch (e: any) {
            toast(e, `Kunde inte ${id ? 'uppdatera' : 'skapa'} gruppen, försök igen`);
        }
    }, [id, entity, navigate]);

    const onDelete = React.useCallback(async () => {
        try {
            if (id) {
                await deleteGroup(id);
                toast('Gruppen borttagen');
                setShowWatchlistDeletionInfo(true);
            }
        } catch (e: any) {
            toast(e, 'Kunde inte ta bort gruppen, försök igen');
        }
    }, [id]);

    return (
        <FullForm
            title={`${id ? 'Redigera' : 'Skapa'} grupp`}
            entity={entity}
            onSubmit={onSubmit}
            onDelete={id ? onDelete : undefined}
        >
            <Popup
                open={showWatchlistDeletionInfo}
                title="Borttagna bevakningar"
                body="Samtliga bevakningar knutna till gruppen har tagit bort."
                okLabel="Ok"
                handleOk={() => navigate(-2)}
            />
            <Grid container spacing={2}>
                <Grid item md={12} lg={6}>
                    <SectionDivider label="Kontaktuppgifter" marginTop={1} />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <AutocompleteField<{
                                id: number | string,
                                name: string,
                                orgNumber?: string
                            }>
                                label="Företag"
                                optionIdKey="id"
                                defaultOption={!entity.ownerTenantId ? null : {
                                    id: entity.ownerTenantId,
                                    name: entity.ownerTenantName || ''
                                }}
                                fetchOptions={(query) => searchTenants(query || '')}
                                minQueryLength={3}
                                getOptionName={(o) => o.name}
                                getOptionDescription={(o) => o.orgNumber || ''}
                                onChange={(tenant) => onChange({
                                    ownerTenantId: tenant === null ? null : tenant.id,
                                    ownerTenantName: tenant === null ? null : tenant.name,
                                    ownerTenantOrgNumber: tenant === null ? null : tenant.orgNumber,
                                })}
                                disabled={!!id || !!tenantId}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="name"
                                label="Namn"
                                value={entity.name}
                                onChange={onChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="description"
                                label="Beskrivning"
                                value={entity.description}
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <BoolField
                                name="isPublic"
                                label="Publik"
                                value={entity.isPublic}
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <BoolField
                                name="canPublishDefault"
                                label="Nya medlemmar kan publicera"
                                value={entity.canPublishDefault}
                                onChange={onChange}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </FullForm>
    );
}
